import { Dispatch, SetStateAction, useState } from "react"
import styled from "styled-components"

import { spacing, TSelectOption } from "@minuthq/meatball-ui-react"

import { HomeFilterDropdownSelect } from "src/components/Filter/HomeFilterDropdown"
import { SearchFilter } from "src/components/Filter/SearchFilter"
import {
  TTaskFilterType,
  usePostTaskFiltersApplied,
  usePostTaskFiltersViewed,
  usePostTaskSearched,
} from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useFetchHomeGroups } from "src/data/homeGroups/queries/homeGroupQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchMembers } from "src/data/organizations/queries/memberQueries"
import { createUnassignedOption } from "src/data/taskManagement/taskManagementFilters"
import { useTranslate } from "src/i18n/useTranslate"
import { DateRangePicker } from "src/ui/DateRangePicker/DateRangePicker"
import { DropdownMultiSelect } from "src/ui/DropdownSelect/DropdownMultiSelect"
import { Show } from "src/ui/Show/Show"

export function TaskFilters({
  homeIds,
  setHomeIds,
  assigneeIds,
  setAssigneeIds,
  searchString,
  setSearchString,
  dateRange,
  setDateRange,
  homeGroupIds,
  setHomeGroupIds,
}: {
  homeIds: string[]
  setHomeIds: Dispatch<SetStateAction<string[]>>
  assigneeIds?: string[]
  setAssigneeIds?: Dispatch<SetStateAction<string[]>>
  searchString?: string
  setSearchString?: Dispatch<SetStateAction<string>>
  dateRange?: { startDate: Date | null; endDate: Date | null }
  setDateRange?: Dispatch<
    SetStateAction<{
      startDate: Date | null
      endDate: Date | null
    }>
  >
  homeGroupIds: string[]
  setHomeGroupIds: Dispatch<SetStateAction<string[]>>
}) {
  const { t, langKeys } = useTranslate()

  const postTaskFiltersViewed = usePostTaskFiltersViewed()
  const postTaskFiltersApplied = usePostTaskFiltersApplied()
  const postTaskSearched = usePostTaskSearched()

  function handleTaskFiltersViewed(filterType: TTaskFilterType) {
    postTaskFiltersViewed.mutate({
      filter_type: filterType,
    })
  }

  function handleSearchChanged(searchValue: string) {
    setSearchString?.(searchValue)

    postTaskSearched.mutate()
  }

  return (
    <FilterRowContainer>
      <Show if={!!setSearchString}>
        <SearchFilter
          placeholder={t(langKeys.task_management_search_for_task)}
          onChange={handleSearchChanged}
          initialValue={searchString}
          delay={800}
          disabled={false}
        />
      </Show>

      <HomeFilterDropdownSelect
        homeIds={homeIds}
        setHomeIds={setHomeIds}
        onHomeSelected={() => {
          postTaskFiltersApplied.mutate({
            filter_type: "unit",
          })
        }}
        onDropdownClick={() => handleTaskFiltersViewed("unit")}
      />

      <Show if={!!setAssigneeIds}>
        <AssigneeDropdownSelect
          assigneeIds={assigneeIds ?? []}
          setAssigneeIds={(assigneeIds) => {
            setAssigneeIds?.(assigneeIds)
            postTaskFiltersApplied.mutate({
              filter_type: "assignee",
            })
          }}
          onDropdownClick={() => handleTaskFiltersViewed("assignee")}
        />
      </Show>

      <Show if={!!setDateRange}>
        <DateRangePicker
          startDate={dateRange?.startDate ?? null}
          endDate={dateRange?.endDate ?? null}
          onDateRangeChange={(dateRange) => {
            setDateRange?.(dateRange)
            postTaskFiltersApplied.mutate({
              filter_type: "date",
            })
          }}
          futureDatesSelectable={true}
          onButtonClick={() => handleTaskFiltersViewed("date")}
          onSelectPreset={({ clearSelection }) => {
            clearSelection && setDateRange?.({ startDate: null, endDate: null })
          }}
        />
      </Show>

      <BuildingDropdownSelect
        selectedBuildingIds={homeGroupIds}
        setSelectedBuildingIds={(homeGroupIds) => {
          setHomeGroupIds(homeGroupIds)
          postTaskFiltersApplied.mutate({
            filter_type: "building",
          })
        }}
        onDropdownClick={() => handleTaskFiltersViewed("building")}
      />
    </FilterRowContainer>
  )
}

function AssigneeDropdownSelect({
  assigneeIds,
  setAssigneeIds,
  onDropdownClick,
}: {
  assigneeIds: string[]
  setAssigneeIds: Dispatch<SetStateAction<string[]>>
  onDropdownClick?: () => void
}) {
  const [searchString, setSearchString] = useState("")
  const { orgId } = useOrganization()
  const { t, langKeys } = useTranslate()
  const { data: users } = useFetchMembers({
    orgId,
    filter: {
      full_name: searchString.length > 0 ? searchString : undefined,
    },
  })

  const isSearching = searchString.length > 0
  const showUnassignedOption = !isSearching

  function handleAssigneeChange({
    checked,
    option,
  }: {
    checked: boolean
    option: TSelectOption<string>
  }) {
    if (checked) {
      setAssigneeIds((prev) => Array.from(new Set([...prev, option.value])))
    } else {
      setAssigneeIds((prev) => prev.filter((o) => o !== option.value))
    }
  }

  const unassignedOption = createUnassignedOption(t)

  const handleAssigneeSearch = (value: string) => {
    setSearchString(value)
  }

  const userOptions =
    users?.members?.map((member) => ({
      label: member.name,
      value: member.member_id,
    })) ?? []

  const options = showUnassignedOption
    ? [unassignedOption, ...userOptions]
    : userOptions

  return (
    <DropdownMultiSelect
      label={t(langKeys.task_management_assignee)}
      placeholder={t(langKeys.task_management_search_for_assignee)}
      options={options}
      selectedValues={assigneeIds}
      onChange={handleAssigneeChange}
      onClearSelection={() => setAssigneeIds([])}
      onSearch={handleAssigneeSearch}
      onClick={onDropdownClick}
    />
  )
}

function BuildingDropdownSelect({
  selectedBuildingIds,
  setSelectedBuildingIds,
  onDropdownClick,
}: {
  selectedBuildingIds: string[]
  setSelectedBuildingIds: Dispatch<SetStateAction<string[]>>
  onDropdownClick?: () => void
}) {
  const { orgId } = useOrganization()
  const { t, langKeys } = useTranslate()
  const [searchString, setSearchString] = useState("")
  const fetchHomeGroups = useFetchHomeGroups({
    orgId,
    filter: { name: searchString.length > 0 ? searchString : undefined },
  })
  const homegroups = fetchHomeGroups.data?.homegroups ?? []

  const buildingOptions = homegroups
    .filter((hg) => hg.type === "building")
    .map((hg) => ({
      label: hg.name,
      value: hg.id,
    }))

  function handleBuildingChange({
    checked,
    option,
  }: {
    checked: boolean
    option: TSelectOption<string>
  }) {
    if (checked) {
      setSelectedBuildingIds((prev) =>
        Array.from(new Set([...prev, option.value]))
      )
    } else {
      setSelectedBuildingIds((prev) => prev.filter((o) => o !== option.value))
    }
  }

  return (
    <DropdownMultiSelect
      label={t(langKeys.home_group_building_plural)}
      placeholder={t(langKeys.search)}
      options={buildingOptions}
      selectedValues={selectedBuildingIds}
      onChange={handleBuildingChange}
      onSearch={setSearchString}
      onClearSelection={() => setSelectedBuildingIds([])}
      loading={fetchHomeGroups.isLoading}
      onClick={onDropdownClick}
    />
  )
}

const FilterRowContainer = styled.div`
  display: flex;
  gap: ${spacing.M};
  align-items: center;
  flex-wrap: wrap;
`
